import React, { Component, useRef } from 'react';
import { func, number, oneOfType, string } from 'prop-types';
import classNames from 'classnames';
import { Heading } from '@thd-olt-component-react/heading';
import { ExperienceContext, useStoreId } from '@thd-nucleus/experience-context';
import { Col } from '@thd-olt-component-react/grid';
import { Rating } from '@thd-olt-component-react/rating';
import { Price } from '@thd-olt-component-react/price';
import { LoadingPlaceholder } from '@thd-olt-component-react/loading-placeholder';
import { CheckAvailability } from '@thd-olt-component-react/check-availability';
import { QueryProvider } from '@thd-nucleus/data-sources';
import { Card } from '@thd-olt-component-react/card';
import { Image } from '@thd-olt-component-react/image';
import './feature-based-recommendations.scss';
import { useProductDeprecated, useFBRDeprecated } from '@thd-nucleus/data-sources/react/dataModel/migration';
import { dataModel } from './dataModel';

const FBRLoader = ({ itemId, children, productDomains }) => {
  const hasFbrRef = useRef(false);
  const storeId = useStoreId();
  const loadedStoreId = useRef(storeId);

  const itemResponse = useProductDeprecated({ itemId, storeId, domains: ['taxonomy'] });

  const taxonomy = itemResponse.data?.product?.taxonomy;

  const fbrResponse = useFBRDeprecated({
    itemId,
    storeId,
    productDomains,
  });
  const data = fbrResponse.data;
  const loading = itemResponse.loading || fbrResponse.loading;
  const error = itemResponse.error || fbrResponse.error;

  if (fbrResponse.data && !fbrResponse.loading && fbrResponse.called) {
    loadedStoreId.current = fbrResponse.variables?.storeId;
  }

  const hasFbr = !!fbrResponse?.data?.fbr;

  return children({
    data, loading, error, hasFbr, taxonomy, loadedStoreId: loadedStoreId.current
  });
};

class FeatureBasedRecommendations extends Component {

  static contextType = ExperienceContext;// eslint-disable-line

  static publish(type, opts) {
    if (typeof window !== 'undefined' && window.THD_EVENT_BUS) {
      window.THD_EVENT_BUS.trigger(type, opts);
    }
  }

  onMouseOver = (event, item) => {
    const { onHover } = this.props;
    if (onHover) {
      onHover(event, item);
    }
  };

  getRowTitles = (data, isMajorAppliance) => {
    const { fbrItems } = data?.fbr || {};
    const commonTitles = ['imageUrl', 'Brand', 'Name', 'Price', 'Ratings'];
    const features = new Set(fbrItems.reduce((acc, item) => {
      const { recommendedFeatures = [] } = item;
      const values = (recommendedFeatures || []).map(({ name }) => name);
      return acc.concat(values);
    }, []));
    if (features.size) {
      commonTitles.push(...Array.from(features));
    }
    if (isMajorAppliance) {
      commonTitles.push('Check Availability');
    }
    commonTitles.push('canonicalUrl');
    return commonTitles;
  };

  getRecommendedValuesPerTitle = (data, title, addTitle) => {
    const { fbrItems } = data?.fbr || {};
    const features = fbrItems
      .map((item, itemIndex) => {
        const { recommendedFeatures = [] } = item;
        const feature = (recommendedFeatures || []).find(({ name }) => name === title);
        if (!feature || feature.name === null) return ' ';
        if (addTitle) {
          return (
            <>
              { itemIndex === 0 && (
                <div className="fbr__cell--titled__title">
                  {title}
                </div>
              )}
              <div className="fbr__cell--titled__value">
                {feature.value}
              </div>
            </>
          );
        }
        return feature.value;
      })
      .filter((i) => i);
    return [...features];
  };

  eventClick = ({
    index, itemId, target, recommendedFeatureSet, recommendedProductSet
  }) => {

    FeatureBasedRecommendations.publish('feature-based-recommendations.click', {
      index, itemId, target, recommendedFeatureSet, recommendedProductSet
    });
  };

  mapFbrValues = (data, loadedStoreId, isMajorAppliance, extendedData = true, isOverlay = false) => {
    const {
      anchorItemId = '',
      fbrItems = [],
      recommendedFeatureSet = '',
      recommendedProductSet = ''
    } = data?.fbr || {};
    const { onClick, onHover } = this.props;
    const { channel } = this.context;
    const rowTitles = this.getRowTitles(data, isMajorAppliance);
    const imageUnavailableURL = 'https://images.homedepot-static.com/catalog/productImages/No_Image_145.jpg';
    const commonTitles = ['imageUrl', 'Brand', 'Name', 'Price', 'Ratings', 'canonicalUrl'];
    if (isMajorAppliance) {
      commonTitles.splice(commonTitles.indexOf('canonicalUrl'), 0, 'Check Availability');
    }
    return rowTitles.map((title) => {
      const ret = channel === 'desktop' ? [title] : [];
      let values = fbrItems.map((item, index) => {
        const { identifiers, media, pricing, reviews } = item?.product || {};
        const { brandName, canonicalUrl, itemId, productLabel } = identifiers || {};
        const { images } = media || {};
        const { mapAboveOriginalPrice = false, mapAboveSpecialPrice = false, mapScenario = false } = pricing || {};
        const { ratingsReviews } = reviews || {};
        const { averageRating = 0, totalReviews = 0 } = ratingsReviews || {};
        const primary = (images || []).find((img) => img.subType === 'PRIMARY');
        const brandNameWithEmptyCase = brandName === null ? ' ' : brandName;
        const productLabelWithEmptyCase = productLabel === null ? ' ' : productLabel;

        // // if there is no image data no sense of showing things. bad data scenario
        // if (!primary) return null;
        let imageUrl = imageUnavailableURL;
        if (primary) {
          const sizes = primary.sizes
            .filter((size) => size === '100' || size === '145');
          sizes.sort((a, b) => b - a);// eslint-disable-line
          if (sizes.length) {
            imageUrl = primary.url.replace('<SIZE>', sizes[0]);
          }
        }

        let optionalProps = {};
        if (title === 'imageUrl') {
          if (onHover) {
            optionalProps.onMouseOver = (event) => this.onMouseOver(event, item);
          }
          return (
            <a
              /* eslint-disable-next-line react/jsx-props-no-spreading */
              {...optionalProps}
              href={canonicalUrl}
              onClick={(event) => {
                this.eventClick({
                  index,
                  itemId: anchorItemId,
                  target: 'product image',
                  recommendedFeatureSet,
                  recommendedProductSet
                });
                if (onClick) {
                  onClick(event, item);
                }
              }}
            >
              <img
                height="145"
                width="145"
                className="fbr__img"
                src={imageUrl}
                alt={productLabel}
              />
            </a>
          );
        }
        if (title === 'Brand') {
          return brandNameWithEmptyCase;
        }
        if (title === 'Name') {
          return productLabelWithEmptyCase;
        }
        if (title === 'Price') {
          if (mapAboveOriginalPrice || mapAboveSpecialPrice || mapScenario) {
            return <div>Add to Cart to See Price</div>;
          }

          return (
            <Price
              channel={channel}
              storeId={loadedStoreId}
              displayEachUom={false}
              itemId={itemId}
              large={false}
            />
          );
        }

        if (title === 'Ratings') {
          const value = parseFloat(averageRating);
          return (
            <>
              <Rating value={value} className="fbr__cell__rating" />
              <span className="fbr__cell__reviews">({totalReviews})</span>
            </>
          );
        }

        if (isMajorAppliance && title === 'Check Availability') {
          return (
            <>
              <div className="fbr__cell__check-availability">
                <CheckAvailability
                  itemId={itemId}
                  isMajorAppliance
                  hideInput
                  autoCheck
                />
              </div>
            </>
          );
        }
        if (title === 'canonicalUrl' && (channel === 'desktop' || isOverlay)) {
          if (onHover) {
            optionalProps.onMouseOver = (event) => this.onMouseOver(event, item);
          }
          return (
            <a
              /* eslint-disable-next-line react/jsx-props-no-spreading */
              {...optionalProps}
              href={canonicalUrl}
              className="fbr__link"
              onClick={(event) => {
                this.eventClick({
                  index,
                  itemId: anchorItemId,
                  target: 'link',
                  recommendedFeatureSet,
                  recommendedProductSet
                });
                if (onClick) {
                  onClick(event, item);
                }
              }}
            >
              View Product
            </a>
          );
        }
        return item[title];
      });
      values = values.filter((n) => n);
      ret.push(...values);
      if (title && commonTitles.indexOf(title) === -1 && extendedData) {
        const features = this.getRecommendedValuesPerTitle(data, title, channel === 'mobile');
        if (features.length) {
          ret.push(...features);
        }
      }
      return ret;
    });
  };

  fbrRows = (values, data, isMajorAppliance) => {
    const { row, rowIndex, currentItemIndex, isOverlay = false } = values;
    const { fbrItems } = data?.fbr || [];
    let { itemId } = this.props;
    const { channel } = this.context;
    const titledRow = row[0]?.props?.children?.[0]?.props?.className.indexOf('fbr__cell--titled') > -1;

    if (!row || !row.length) return undefined;
    return row.map((eachColumn, eachColumnIndex) => {
      const classes = classNames('fbr__cell', {
        'fbr__cell--current': currentItemIndex === eachColumnIndex
      });
      const isCheckAvailability = (eachColumn === 'Check Availability');
      const majorApplianceClasses = classNames('fbr__cell',
        { 'fbr__cell--check-availability-input': isCheckAvailability });
      const tdKey = `fbr-r${eachColumnIndex}-data`;
      if (eachColumnIndex === 0 && ['imageUrl', 'canonicalUrl'].indexOf(eachColumn) > -1) {
        return <td key={tdKey} className="fbr__cell" datatype="product" />;
      }
      if (isMajorAppliance && isCheckAvailability) {
        return (
          <td key={tdKey} className={majorApplianceClasses} datatype="product">
            <CheckAvailability
              itemId={itemId}
              isMajorAppliance
              inputHeaderTitle="Check Earliest Delivery Date"
              defaultMessaging={false}
            />
          </td>
        );
      }
      if (rowIndex === 0 && (channel !== 'desktop' || eachColumnIndex !== 0)) {
        itemId = fbrItems[eachColumnIndex - 1];
        return (
          <td key={tdKey} className={classes} datatype="product">
            <meta data-prop="productID" content={itemId} />
            {eachColumnIndex === currentItemIndex && (
              <div className="fbr__current-banner">current product</div>
            )}
            {eachColumn}
          </td>
        );
      }
      return (
        <td key={tdKey} className={classes}>
          {eachColumn}
        </td>
      );
    });
  };

  render() {
    const { itemId } = this.props;
    return (
      <FBRLoader
        itemId={itemId}
        productDomains={['identifiers', 'media', 'pricing', 'reviews', 'info']}
      >
        {({
          data, loading, error, hasFbr, taxonomy, loadedStoreId
        }) => {
          if (!data && loading) {
            return (
              <LoadingPlaceholder
                type="text"
                ready={false}
                color="#E0E0E0"
                rows={4}
                showLoadingAnimation
              >
                Loading...
              </LoadingPlaceholder>
            );
          }
          if (error && !data) {
            return <pre><code>{error.toString()}</code></pre>;
          }
          if (!hasFbr) {
            return null;
          }
          const { fbrItems } = data?.fbr || {};
          if (!data.fbr || !fbrItems || !fbrItems.length) {
            return null;
          }
          let sortedItems = fbrItems.slice();
          const { channel } = this.context;
          if (channel === 'mobile') {
            sortedItems = fbrItems.slice().sort((first, next) => {
              if (first.product.itemId === itemId) return -1;
              if (next.product.itemId === itemId) return 1;
              return 0;
            });
          }

          const isMajorAppliance = fbrItems.every((item) => {
            return item.product?.identifiers?.productType === 'MAJOR_APPLIANCE';
          });
          const sortedData = { ...data, fbr: { ...data.fbr, fbrItems: sortedItems } };

          const tableData = this.mapFbrValues(sortedData, loadedStoreId, isMajorAppliance);
          const cardTableData = this.mapFbrValues(sortedData, loadedStoreId, isMajorAppliance, false);
          const overlayTableData = this.mapFbrValues(sortedData, loadedStoreId, isMajorAppliance, true, true);

          let titleLabel = 'Products';
          if (channel === 'desktop') {
            const { breadCrumbs } = taxonomy || {};
            const lastBreadCrumbLabel = breadCrumbs && breadCrumbs[breadCrumbs.length - 1].label;
            titleLabel = lastBreadCrumbLabel || 'Products';
          }

          let currentItemIndex = sortedItems.findIndex((item) => {
            return String(item.product.identifiers.itemId) === itemId;
          });
          if (channel === 'desktop') currentItemIndex += 1;

          return (
            <QueryProvider dataSource="FBR">
              <Card className="fbr__card--top">
                <div className="fbr__card grid">
                  <Col flatten>
                    <Heading title={`Compare Similar ${titleLabel}`} underline />
                  </Col>
                  <table className="fbr col__12-12" datatype="container">
                    <tbody className={classNames('fbr__body', { 'fbr__body--mobile': channel === 'mobile' })}>
                      {tableData.map((row, rowIndex) => {
                        return (
                          <tr key={`fbr-${rowIndex}`}>
                            {this.fbrRows({
                              row,
                              rowIndex,
                              currentItemIndex
                            }, sortedData, isMajorAppliance)}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <Card.Content
                  title={`Compare Similar ${titleLabel}`}
                  className="fbr__card"
                  maxHeight="9999px"
                  fade={channel === 'mobile'}
                >
                  <div className="grid">
                    <table className="fbr col__12-12" datatype="container">
                      <tbody className={classNames('fbr__body', { 'fbr__body--mobile': channel === 'mobile' })}>
                        {cardTableData.map((row, rowIndex) => {
                          if (!row.length) return undefined;
                          return (
                            <tr key={`fbr-${rowIndex}`}>
                              {this.fbrRows({
                                row,
                                rowIndex,
                                currentItemIndex
                              }, sortedData, isMajorAppliance)}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <div className="fbr__show-more">
                      <Image asset="caret-orange.svg" alt="" />
                    </div>

                  </div>
                  <Card.Content.Overlay overlayTitle={null} className="fbr__card__overlay">
                    <div className="grid">
                      <table className="fbr col__12-12" datatype="container">
                        <tbody className={classNames('fbr__body', { 'fbr__body--mobile': channel === 'mobile' })}>
                          {overlayTableData.map((row, rowIndex) => {
                            const firstValue = row[0]?.props?.children?.[0]?.props?.className || '';
                            const titledRow = firstValue.indexOf('fbr__cell--titled') > -1;
                            const classes = classNames({
                              'fbr__row--titled': titledRow,
                            });
                            return (
                              <tr className={classes} key={`fbr-${rowIndex}`}>
                                {this.fbrRows({
                                  row,
                                  rowIndex,
                                  currentItemIndex,
                                  isOverlay: true,
                                }, sortedData, isMajorAppliance)}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </Card.Content.Overlay>
                </Card.Content>
              </Card>
            </QueryProvider>
          );
        }}
      </FBRLoader>
    );
  }
}

FeatureBasedRecommendations.propTypes = {
  itemId: string.isRequired,
  onClick: func,
  onHover: func,
  productType: string,
  storeId: oneOfType([number, string])
};

FeatureBasedRecommendations.defaultProps = {
  onClick: null,
  onHover: null,
  productType: 'Products',
  storeId: null
};

FeatureBasedRecommendations.dataModel = dataModel;

export default FeatureBasedRecommendations;
